import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSearchbar,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import {
  closeOutline,
  documentTextOutline,
  informationCircleOutline,
  paperPlaneOutline,
  saveOutline,
  trashOutline,
} from 'ionicons/icons';
import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useCollettivaEnabled,
  useEnqueueMaster,
  useEvents,
  useMasters,
  useStockProjection,
  useTransportDocumentsAccessGate,
  useWarehouseEnabled,
} from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { useBackGuard } from '../../lib/hooks/useBackGuard.js';
import { projectedCarryingCost } from '../../lib/stockCarryingCost.js';
import { shipmentLineStockEffectV1 } from '../../lib/shipmentStockPolicy.js';
import { findExactThenLegacyStockEvent } from '../../lib/stockEventBusinessScope.js';
import {
  canonicalProductionLot,
  draftReservedQtyForStockLocator,
  resolveProductionQuantityLimit,
  selectProductionCandidates,
  validateStockLocatorTotals,
  type ProductionQuantityLimitError,
} from '../../lib/transport/productionCandidates.js';
import {
  buildShipmentStockVoidCommand,
  convertQty,
  formatShipmentNumber,
  shipmentStockVoidCommandIdFor,
  ShipmentDispatchedPayloadSchema,
  StockCommandV1Schema,
  type StockCommandV1,
  type ShipmentLine,
} from '@trackeat/shared';

// ───────── tipi locali ─────────

interface BusinessRow {
  id: string;
  tenant_id: string;
  name: string;
  archived_at?: string | null;
}

interface FoodRow {
  id: string;
  name: string;
  default_uom?: string | null;
  recipe_id?: string | null;
  archived_at?: string | null;
}

interface ProductionRow {
  id: string;
  business_id?: string | null;
  output_name: string;
  output_qty: number | null;
  output_uom: string | null;
  produced_at: string;
  expires_at?: string | null;
  recipe_id?: string | null;
  lp_code?: string | null;
  archived_at?: string | null;
}

// Eventi stock_in/stock_out come letti dal mirror locale (vedi WarehousePage).
interface StockEventRow {
  id: string;
  client_ts: string;
  // T-221: sede del movimento (events.business_id); NULL sugli eventi storici
  // → validi per ogni sede.
  business_id?: string | null;
  payload: {
    business_id?: string | null;
    food_id: string;
    qty: number;
    uom?: string;
    batch_lot: string | null;
    lot_closed?: boolean;
    expiration_date?: string | null;
    supplier_id?: string | null;
    document_id?: string | null;
    storage_unit_id?: string | null;
    stock_unit_cost?: number | null;
    unit_cost?: number | null;
    source_currency?: string | null;
    currency?: string | null;
    void_compensation?: boolean;
  };
}

interface ShipmentRow {
  id: string;
  tenant_id: string;
  business_id: string;
  kind: 'ddt' | 'trasferimento';
  document_scope?: 'collective' | 'restaurant';
  status: 'draft' | 'issued' | 'void';
  doc_number: number | null;
  doc_year: number | null;
  destination_id: string | null;
  to_business_id: string | null;
  transport_cause?: string | null;
  vehicle_plate?: string | null;
  lines: ShipmentLine[];
  notes?: string | null;
  issued_at?: string | null;
  created_at?: string;
  version?: number;
  archived_at?: string | null;
}

// Lotto con giacenza residua > 0, aggregato dagli eventi stock_in/stock_out.
interface AvailableLot {
  food_id: string;
  lot: string;
  qty: number;
  uom: string;
}

interface QtyModalState {
  production_id: string | null;
  food_id: string;
  foodName: string;
  lot: string;
  maxQty: number;
  limitKind: 'availability' | 'production';
  stockEffect: 'dispatch' | 'none';
  uom: string;
}

interface RestaurantLotCandidate {
  production: ProductionRow;
  food_id: string | null;
  lot: string;
  declaredQty: number | null;
  uom: string;
  availableQty: number | null;
  maxQty: number | null;
  disabledReason: string | null;
}

type TransferPageFlow = 'collective' | 'restaurant';

function fmtQty(qty: number): string {
  return Number.isInteger(qty) ? String(qty) : qty.toFixed(2);
}

function productionLimitErrorMessage(error: ProductionQuantityLimitError): string {
  switch (error) {
    case 'invalid_output_qty': return 'quantità prodotta non disponibile';
    case 'missing_output_uom': return 'unità di misura della produzione mancante';
    case 'missing_food_uom': return 'unità del prodotto finito non configurata';
    case 'production_food_uom_mismatch': return 'unità della produzione non coerente col prodotto finito';
    case 'missing_balance': return 'lotto senza saldo nel magazzino';
    case 'production_balance_uom_mismatch': return 'unità della produzione non coerente col saldo del lotto';
    case 'no_available_stock': return 'lotto esaurito o già impegnato nel documento';
  }
}

// ───────── stile "form sezionato" (solo presentazione) ─────────

// Header di sezione numerato (1 · SEDE DI DESTINAZIONE, …).
const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  color: '#475569',
  letterSpacing: '0.05em',
  margin: '18px 4px 8px',
};

// Card con stripe laterale di completamento (pattern LabelsPage step 2):
// ambra = manca un campo obbligatorio, verde = sezione completa.
const stripedCardStyle: React.CSSProperties = {
  display: 'flex',
  background: '#fff',
  borderRadius: 14,
  marginBottom: 10,
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
  overflow: 'hidden',
};

function stripeStyle(complete: boolean): React.CSSProperties {
  return { width: 4, background: complete ? '#10b981' : '#f59e0b', flexShrink: 0, alignSelf: 'stretch' };
}

const plainCardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
  overflow: 'hidden',
};

// Badge di stato documento (lista trasferimenti recenti).
type TransferStatus = ShipmentRow['status'];
const STATUS_BADGE: Record<TransferStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'BOZZA', color: '#f59e0b', bg: '#fef3c7' },
  issued: { label: 'EMESSO', color: '#15803d', bg: '#dcfce7' },
  void: { label: 'ANNULLATO', color: '#dc2626', bg: '#fef2f2' },
};

function statusBadgeStyle(status: TransferStatus): React.CSSProperties {
  const b = STATUS_BADGE[status];
  return {
    fontSize: 11,
    fontWeight: 700,
    color: b.color,
    background: b.bg,
    borderRadius: 6,
    padding: '2px 8px',
    letterSpacing: '0.04em',
  };
}

/**
 * Schermata SCARICO/TRASFERIMENTO del magazziniere (T-215, modulo collettiva):
 * documenta la merce consegnata dal magazzino centrale all'impianto di
 * lavorazione (kind='trasferimento').
 *
 * T-221: ogni sede ha il suo magazzino. L'EMISSIONE del trasferimento genera
 * i movimenti: stock_out per riga/lotto dalla sede mittente + stock_in
 * speculare sulla sede di destinazione. Le BOZZE riservano la giacenza
 * (sottratte dal disponibile mostrato) senza generare eventi — niente
 * compensazioni sulla hash chain se la bozza viene modificata o eliminata.
 */
function TransferPageContent({ flow }: { flow: TransferPageFlow }): JSX.Element {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const collectiveFlow = flow === 'collective';
  const collettivaEnabled = useCollettivaEnabled();
  const transportAccess = useTransportDocumentsAccessGate();
  const warehouseEnabled = useWarehouseEnabled();
  const {
    data: businesses = [],
    isLoading: businessesLoading,
    isError: businessesError,
  } = useMasters<BusinessRow>('businesses');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { data: productions = [] } = useMasters<ProductionRow>('productions');
  const canonicalUomByFood = useMemo(
    () => Object.fromEntries(
      foods
        .filter((food) => !food.archived_at && food.default_uom?.trim())
        .map((food) => [food.id, food.default_uom!]),
    ),
    [foods],
  );
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const { data: shipments = [] } = useMasters<ShipmentRow>('shipments');
  // Tutti gli eventi stock (limit null = nessun cap: la giacenza per lotto
  // dev'essere completa — memory feedback_no_app_side_limits_for_user_features).
  const { data: stockIn = [] } = useEvents('stock_in', null);
  const enqueueMaster = useEnqueueMaster();

  // ── stato form bozza ──
  const [toBusinessId, setToBusinessId] = useState('');
  const [lines, setLines] = useState<ShipmentLine[]>([]);
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [notes, setNotes] = useState('');
  // Bozza in modifica (ripresa dalla lista): id + version corrente per l'OCC.
  const [editing, setEditing] = useState<{ id: string; version: number } | null>(null);

  const [lotSearch, setLotSearch] = useState('');
  const [productionScope, setProductionScope] = useState<'today' | 'history'>('today');
  const [qtyModal, setQtyModal] = useState<QtyModalState | null>(null);
  const [qtyInput, setQtyInput] = useState('');
  const [busy, setBusy] = useState<null | 'save' | 'issue' | 'pdf'>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Documento appena emesso: card di successo con "Apri PDF".
  const [justIssued, setJustIssued] = useState<{ id: string; doc_number: number; doc_year: number } | null>(null);
  // Numeri assegnati in sessione: il mirror locale resta 'draft' finché il
  // pull non aggiorna la riga; questo merge tiene la lista coerente subito.
  const [issuedLocal, setIssuedLocal] = useState<Record<string, { doc_number: number; doc_year: number }>>({});
  const [voidedLocal, setVoidedLocal] = useState<Set<string>>(() => new Set());
  // T-295: bersagli per annullo (trasferimento emesso) e elimina (bozza).
  const [voidTarget, setVoidTarget] = useState<ShipmentRow | null>(null);
  // T-295 fix #5: guard sincrono anti doppio-annullo (doppio tap / re-entrancy)
  // → eviterebbe DUE set di eventi compensativi sulla stessa giacenza.
  const voidBusy = useRef(false);
  const [deleteTarget, setDeleteTarget] = useState<ShipmentRow | null>(null);

  useBackGuard(!!qtyModal, () => setQtyModal(null));

  const tenantBusinesses = useMemo(
    () => businesses.filter(
      (business) =>
        business.tenant_id === profile?.active_tenant_id
        && !business.archived_at,
    ),
    [businesses, profile?.active_tenant_id],
  );
  // Sedi di destinazione: tutte le sedi del tenant TRANNE quella attiva.
  const otherBusinesses = useMemo(
    () =>
      tenantBusinesses.filter(
        (b) =>
          b.id !== profile?.active_business_id,
      ),
    [tenantBusinesses, profile?.active_business_id],
  );

  // Giacenza residua PER LOTTO dal projector canonico: usa stock_qty/stock_uom
  // quando presenti, converte solo UdM compatibili e non somma mai coordinate
  // eterogenee. Il projector include tutte le bozze come riserve fino
  // all'emissione atomica.
  const activeBusinessId = profile?.active_business_id ?? null;
  const allowLegacyUnscopedBusiness = !businessesLoading
    && !businessesError
    && activeBusinessId !== null
    && tenantBusinesses.length === 1
    && tenantBusinesses[0]?.id === activeBusinessId;
  const availableLots = useMemo<AvailableLot[]>(() => {
    const map = new Map<string, AvailableLot>(
      stockProjection.balances
        .filter((balance) => !!balance.batchLot && !balance.closed)
        .map((balance) => [
          `${balance.foodId}|${balance.batchLot}`,
          {
            food_id: balance.foodId,
            lot: balance.batchLot!,
            qty: balance.availableQty,
            uom: balance.uom,
          },
        ]),
    );
    // Durante la modifica riapriamo solo la riserva già appartenente alla
    // bozza corrente. Tutte le altre sono già sottratte dal projector.
    const editingShipment = editing
      ? shipments.find((shipment) =>
          shipment.id === editing.id
          && shipment.status === 'draft'
          && !shipment.archived_at
          && (collectiveFlow
            ? (!activeBusinessId || shipment.business_id === activeBusinessId)
            : activeBusinessId !== null
              && shipment.tenant_id === profile?.active_tenant_id
              && shipment.business_id === activeBusinessId))
      : null;
    if (!collectiveFlow) {
      for (const current of map.values()) {
        current.qty += draftReservedQtyForStockLocator(
          editingShipment?.lines ?? [],
          { foodId: current.food_id, batchLot: current.lot, uom: current.uom },
        );
      }
    } else {
      for (const line of editingShipment?.lines ?? []) {
        if (!line.food_id || !line.batch_lot) continue;
        const current = map.get(`${line.food_id}|${line.batch_lot}`);
        if (!current) continue;
        const reserved = convertQty(Number(line.qty) || 0, line.uom, current.uom);
        if (reserved != null) current.qty += reserved;
      }
    }
    return [...map.values()].filter((l) => l.qty > 0);
  }, [
    stockProjection.balances,
    shipments,
    editing,
    activeBusinessId,
    collectiveFlow,
    profile?.active_tenant_id,
  ]);

  function foodName(id: string): string {
    return foods.find((f) => f.id === id)?.name ?? id.slice(0, 8);
  }
  function foodUom(id: string): string {
    return foods.find((f) => f.id === id)?.default_uom ?? 'pz';
  }
  function businessName(id: string | null): string {
    if (!id) return '—';
    return businesses.find((b) => b.id === id)?.name ?? id.slice(0, 8);
  }
  function transferHasStockLines(shipment: ShipmentRow): boolean {
    return shipment.lines.some((line) => (
      !!line.food_id && shipmentLineStockEffectV1(line, false) === 'dispatch'
    ));
  }

  // Lotti filtrati per nome alimento (searchbar) e ordinati alfabeticamente.
  const filteredLots = useMemo(() => {
    const q = lotSearch.trim().toLowerCase();
    const list = availableLots
      .map((l) => ({ ...l, name: foodName(l.food_id) }))
      .filter((l) => !q || l.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name, 'it') || a.lot.localeCompare(b.lot, 'it'));
    return list;
    // foods entra via foodName: dipendenza esplicita per nomi aggiornati.
  }, [availableLots, lotSearch, foods]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishedFoodsByRecipe = useMemo(() => {
    const map = new Map<string, FoodRow[]>();
    for (const food of foods) {
      if (food.archived_at || !food.recipe_id) continue;
      const current = map.get(food.recipe_id) ?? [];
      current.push(food);
      map.set(food.recipe_id, current);
    }
    return map;
  }, [foods]);

  // Il percorso ristorante parte esclusivamente da una lavorazione della sede.
  // La stessa selezione alimenta sia il picker (oggi/storico) sia i controlli
  // fail-closed prima del salvataggio e dell'emissione.
  const eligibleRestaurantProductions = useMemo(() => {
    const selected = selectProductionCandidates({
      productions: productions.map((production) => ({
        id: production.id,
        business_id: production.business_id ?? null,
        output_name: production.output_name,
        output_qty: production.output_qty,
        output_uom: production.output_uom,
        produced_at: production.produced_at,
        expires_at: production.expires_at ?? null,
        recipe_id: production.recipe_id ?? null,
        lp_code: production.lp_code ?? null,
        archived_at: production.archived_at ?? null,
      })),
      activeBusinessId,
      // T-563 fail-closed: per un documento ristorante la lavorazione deve
      // appartenere esplicitamente alla sede attiva. Le produzioni legacy
      // senza business_id non possono essere attribuite per inferenza.
      allowLegacyUnscopedBusiness: false,
      now: new Date(),
    });
    return [...selected.today, ...selected.history];
  }, [activeBusinessId, productions]);

  const restaurantCandidateById = useMemo(() => {
    const out = new Map<string, RestaurantLotCandidate>();
    for (const production of eligibleRestaurantProductions) {
      const lot = canonicalProductionLot(production);
      const matchingFoods = production.recipe_id
        ? (finishedFoodsByRecipe.get(production.recipe_id) ?? [])
        : [];
      const food = matchingFoods.length === 1 ? matchingFoods[0]! : null;
      const balance = food
        ? availableLots.find((candidate) => (
            candidate.food_id === food.id && candidate.lot === lot
          )) ?? null
        : null;
      const alreadyInForm = food && balance
        ? lines.reduce((total, line) => {
            if (
              line.production_id === production.id
              || line.food_id !== food.id
              || line.batch_lot.trim() !== lot
            ) return total;
            if (line.uom.trim().toLowerCase() !== balance.uom.trim().toLowerCase()) {
              return Number.POSITIVE_INFINITY;
            }
            return total + line.qty;
          }, 0)
        : 0;
      const balanceQty = balance
        ? Math.max(0, balance.qty - alreadyInForm)
        : null;
      const limit = resolveProductionQuantityLimit({
        outputQty: production.output_qty,
        outputUom: production.output_uom,
        warehouseEnabled,
        foodUom: food?.default_uom ?? null,
        balanceQty,
        balanceUom: balance?.uom ?? null,
      });
      let disabledReason = limit.error
        ? productionLimitErrorMessage(limit.error)
        : null;

      if (warehouseEnabled) {
        if (!production.recipe_id) {
          disabledReason = 'ricetta non collegata alla lavorazione';
        } else if (matchingFoods.length === 0) {
          disabledReason = 'prodotto finito non collegato alla ricetta';
        } else if (matchingFoods.length > 1) {
          disabledReason = 'ricetta collegata a più prodotti finiti';
        }
      }

      out.set(production.id, {
        production,
        food_id: warehouseEnabled ? (food?.id ?? null) : null,
        lot,
        declaredQty: limit.declaredQty,
        uom: limit.uom,
        availableQty: warehouseEnabled ? limit.availableQty : null,
        maxQty: limit.maxQty,
        disabledReason,
      });
    }
    return out;
  }, [availableLots, eligibleRestaurantProductions, finishedFoodsByRecipe, lines, warehouseEnabled]);

  const restaurantCandidateBuckets = useMemo(() => {
    const selected = selectProductionCandidates({
      productions: productions.map((production) => ({
        id: production.id,
        business_id: production.business_id ?? null,
        output_name: production.output_name,
        output_qty: production.output_qty,
        output_uom: production.output_uom,
        produced_at: production.produced_at,
        expires_at: production.expires_at ?? null,
        recipe_id: production.recipe_id ?? null,
        lp_code: production.lp_code ?? null,
        archived_at: production.archived_at ?? null,
      })),
      activeBusinessId,
      allowLegacyUnscopedBusiness: false,
      excludedProductionIds: new Set(
        lines
          .map((line) => line.production_id)
          .filter((id): id is string => !!id),
      ),
      search: lotSearch,
      now: new Date(),
    });
    const toCandidates = (rows: typeof selected.today) => rows.flatMap((row) => {
      const candidate = restaurantCandidateById.get(row.id);
      return candidate ? [candidate] : [];
    });
    return {
      today: toCandidates(selected.today),
      history: toCandidates(selected.history),
    };
  }, [
    activeBusinessId,
    lines,
    lotSearch,
    productions,
    restaurantCandidateById,
  ]);
  const restaurantLots = restaurantCandidateBuckets[productionScope];

  // Trasferimenti recenti (bozze + emessi + annullati), più recenti per primi.
  const recentTransfers = useMemo(
    () =>
      shipments
        .filter((s) =>
          s.kind === 'trasferimento'
          && (collectiveFlow
            ? s.document_scope !== 'restaurant'
            : s.document_scope === 'restaurant')
          && !s.archived_at
          && activeBusinessId !== null
          && s.business_id === activeBusinessId)
        .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')),
    [shipments, activeBusinessId, collectiveFlow],
  );

  function isActiveBusinessTransfer(shipment: ShipmentRow): boolean {
    return activeBusinessId !== null
      && shipment.tenant_id === profile?.active_tenant_id
      && shipment.business_id === activeBusinessId
      && (collectiveFlow
        ? shipment.document_scope !== 'restaurant'
        : shipment.document_scope === 'restaurant');
  }

  function resetForm(): void {
    setToBusinessId('');
    setLines([]);
    setVehiclePlate('');
    setNotes('');
    setEditing(null);
  }

  function openQtyModal(lot: AvailableLot): void {
    const existing = lines.find((l) => l.food_id === lot.food_id && l.batch_lot === lot.lot);
    setQtyInput(String(existing?.qty ?? lot.qty));
    setQtyModal({
      production_id: null,
      food_id: lot.food_id,
      foodName: foodName(lot.food_id),
      lot: lot.lot,
      maxQty: lot.qty,
      limitKind: 'availability',
      stockEffect: 'dispatch',
      uom: lot.uom || foodUom(lot.food_id),
    });
  }

  function openRestaurantQtyModal(candidate: RestaurantLotCandidate): void {
    if (candidate.disabledReason || candidate.maxQty == null || candidate.maxQty <= 0) {
      setToast(candidate.disabledReason ?? 'Lavorazione non trasferibile');
      return;
    }
    const existing = lines.find((line) => line.production_id === candidate.production.id);
    setQtyInput(String(existing?.qty ?? candidate.maxQty));
    setQtyModal({
      production_id: candidate.production.id,
      food_id: candidate.food_id ?? '',
      foodName: candidate.production.output_name,
      lot: candidate.lot,
      maxQty: candidate.maxQty,
      limitKind: warehouseEnabled ? 'availability' : 'production',
      stockEffect: warehouseEnabled ? 'dispatch' : 'none',
      uom: candidate.uom,
    });
  }

  // Riapre il modale qty per una riga già aggiunta (edit). Se il lotto non è
  // più in giacenza (consumato altrove nel frattempo), il max resta la qty già
  // inserita: l'utente può solo ridurla o rimuovere la riga.
  function editLine(line: ShipmentLine): void {
    if (!collectiveFlow) {
      const candidate = line.production_id
        ? restaurantCandidateById.get(line.production_id)
        : null;
      if (!candidate || candidate.disabledReason || candidate.maxQty == null || candidate.maxQty <= 0) {
        setToast(candidate?.disabledReason ?? 'La lavorazione collegata non è più disponibile');
        return;
      }
      setQtyInput(String(line.qty));
      setQtyModal({
        production_id: candidate.production.id,
        food_id: candidate.food_id ?? '',
        foodName: candidate.production.output_name,
        lot: candidate.lot,
        maxQty: candidate.maxQty,
        limitKind: warehouseEnabled ? 'availability' : 'production',
        stockEffect: warehouseEnabled ? 'dispatch' : 'none',
        uom: candidate.uom,
      });
      return;
    }
    const avail = availableLots.find(
      (l) => l.food_id === (line.food_id ?? '') && l.lot === line.batch_lot,
    );
    setQtyInput(String(line.qty));
    setQtyModal({
      production_id: null,
      food_id: line.food_id ?? '',
      foodName: line.description,
      lot: line.batch_lot,
      maxQty: avail?.qty ?? line.qty,
      limitKind: 'availability',
      stockEffect: 'dispatch',
      uom: line.uom,
    });
  }

  function confirmQty(): void {
    if (!qtyModal) return;
    const qty = Number(qtyInput.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) {
      setToast('Quantità non valida');
      return;
    }
    if (qty > qtyModal.maxQty) {
      setToast(
        qtyModal.limitKind === 'availability'
          ? `Massimo disponibile: ${fmtQty(qtyModal.maxQty)} ${qtyModal.uom}`
          : `Massimo dichiarato dalla lavorazione: ${fmtQty(qtyModal.maxQty)} ${qtyModal.uom}`,
      );
      return;
    }
    const line: ShipmentLine = {
      production_id: qtyModal.production_id,
      food_id: qtyModal.stockEffect === 'dispatch' ? (qtyModal.food_id || null) : null,
      stock_effect_v1: qtyModal.stockEffect,
      description: qtyModal.foodName,
      batch_lot: qtyModal.lot,
      qty,
      uom: qtyModal.uom,
    };
    setLines((prev) => {
      const idx = prev.findIndex(
        (l) => line.production_id
          ? l.production_id === line.production_id
          : l.food_id === line.food_id && l.batch_lot === line.batch_lot,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = line;
        return next;
      }
      return [...prev, line];
    });
    setQtyModal(null);
    setQtyInput('');
  }

  function removeLine(line: ShipmentLine): void {
    setLines((prev) =>
      prev.filter((candidate) => line.production_id
        ? candidate.production_id !== line.production_id
        : !(candidate.food_id === line.food_id && candidate.batch_lot === line.batch_lot)),
    );
  }

  function resumeDraft(s: ShipmentRow): void {
    if (!isActiveBusinessTransfer(s) || s.status !== 'draft') {
      setToast('Il trasferimento appartiene a un’altra sede o non è più una bozza');
      return;
    }
    setEditing({ id: s.id, version: s.version ?? 0 });
    setToBusinessId(s.to_business_id ?? '');
    setLines(Array.isArray(s.lines) ? s.lines : []);
    setVehiclePlate(s.vehicle_plate ?? '');
    setNotes(s.notes ?? '');
    setJustIssued(null);
  }

  function restaurantLinesForPersistence(): { lines: ShipmentLine[]; error: string | null } {
    const normalized: ShipmentLine[] = [];
    for (const line of lines) {
      if (!line.production_id) {
        return { lines: [], error: 'Ogni riga deve provenire da una lavorazione della sede' };
      }
      const candidate = restaurantCandidateById.get(line.production_id);
      if (!candidate) {
        return { lines: [], error: `La lavorazione collegata a "${line.description}" non è più disponibile` };
      }
      if (candidate.disabledReason || candidate.maxQty == null) {
        return {
          lines: [],
          error: `${candidate.production.output_name}: ${candidate.disabledReason ?? 'quantità non verificabile'}`,
        };
      }
      const qty = Number(line.qty);
      if (!Number.isFinite(qty) || qty <= 0 || qty > candidate.maxQty) {
        const limit = warehouseEnabled ? 'disponibile' : 'dichiarato dalla lavorazione';
        return {
          lines: [],
          error: `${candidate.production.output_name}: massimo ${limit} ${fmtQty(candidate.maxQty)} ${candidate.uom}`,
        };
      }
      normalized.push({
        production_id: candidate.production.id,
        food_id: warehouseEnabled ? candidate.food_id : null,
        stock_effect_v1: warehouseEnabled ? 'dispatch' : 'none',
        description: candidate.production.output_name,
        batch_lot: candidate.lot,
        qty,
        uom: candidate.uom,
      });
    }

    const productionTotals = new Map<string, number>();
    for (const line of normalized) {
      const productionId = line.production_id!;
      productionTotals.set(productionId, (productionTotals.get(productionId) ?? 0) + line.qty);
    }
    for (const [productionId, requestedQty] of productionTotals) {
      const candidate = restaurantCandidateById.get(productionId);
      if (!candidate?.declaredQty || requestedQty > candidate.declaredQty + 1e-9) {
        return {
          lines: [],
          error: `${candidate?.production.output_name ?? 'Lavorazione'}: quantità complessiva superiore a quella prodotta`,
        };
      }
    }

    if (warehouseEnabled) {
      const aggregate = validateStockLocatorTotals(
        normalized,
        availableLots.map((balance) => ({
          food_id: balance.food_id,
          batch_lot: balance.lot,
          available_qty: balance.qty,
          uom: balance.uom,
        })),
      );
      if (!aggregate.valid) {
        return {
          lines: [],
          error: aggregate.error === 'insufficient_available_stock'
            ? `Lotto ${aggregate.batchLot ?? ''}: quantità complessiva ${fmtQty(aggregate.requestedQty ?? 0)} superiore al saldo ${fmtQty(aggregate.availableQty ?? 0)} ${aggregate.uom ?? ''}`
            : `Lotto ${aggregate.batchLot ?? ''}: saldo o unità di misura non verificabili`,
        };
      }
    }
    return { lines: normalized, error: null };
  }

  /**
   * Salva (o aggiorna) la bozza nel master `shipments` — offline-ok.
   * Ritorna id e payload ottimistico, o null se la validazione fallisce.
   * Il payload distingue un ACK concorrente da un conflitto LWW prima della
   * numerazione.
   */
  async function saveDraft(): Promise<{
    id: string;
    expectedPatch: Record<string, unknown>;
    persistedLines: ShipmentLine[];
  } | null> {
    if (!profile?.active_tenant_id || !profile?.active_business_id) {
      setToast('Nessuna sede attiva');
      return null;
    }
    if (!toBusinessId) {
      setToast('Scegli la sede di destinazione');
      return null;
    }
    const restaurantResult = collectiveFlow
      ? null
      : restaurantLinesForPersistence();
    if (restaurantResult?.error) {
      setToast(restaurantResult.error);
      return null;
    }
    const persistedLines = collectiveFlow
      ? lines.map((line) => ({
          ...line,
          stock_effect_v1: shipmentLineStockEffectV1(line, false),
        }))
      : restaurantResult!.lines;
    const isUpdate = editing !== null;
    const baseVersion = editing?.version ?? 0;
    const id = editing?.id ?? crypto.randomUUID();
    if (isUpdate) {
      const current = shipments.find((shipment) => shipment.id === id);
      if (!current || !isActiveBusinessTransfer(current) || current.status !== 'draft') {
        setToast('La bozza appartiene a un’altra sede o è stata modificata: ricarica la pagina');
        return null;
      }
    }
    const optimisticVersion = isUpdate ? baseVersion + 1 : 0;
    const patch: Record<string, unknown> = {
      id,
      tenant_id: profile.active_tenant_id,
      business_id: profile.active_business_id,
      kind: 'trasferimento',
      document_scope: collectiveFlow ? 'collective' : 'restaurant',
      status: 'draft',
      destination_id: null,
      to_business_id: toBusinessId,
      transport_cause: collectiveFlow
        ? 'Trasferimento interno tra sedi'
        : 'Trasferimento interno prodotti finiti',
      vehicle_plate: vehiclePlate.trim() || null,
      lines: persistedLines,
      notes: notes.trim() || null,
    };
    await enqueueMaster.mutateAsync({
      table: 'shipments',
      id,
      op: isUpdate ? 'update' : 'insert',
      base_version: baseVersion,
      patch,
    });
    if (!collectiveFlow) setLines(persistedLines);
    // Convenzione version del mirror locale (useEnqueueMaster, allineata a
    // sync-push): insert → 0, update → base_version + 1. Tracciamo qui la
    // stessa regola per gli update successivi dalla stessa schermata (OCC);
    // resumeDraft la rilegge comunque da row.version via useMasters.
    setEditing({ id, version: optimisticVersion });
    return {
      id,
      expectedPatch: { ...patch, version: optimisticVersion },
      persistedLines,
    };
  }

  async function handleSaveDraft(): Promise<void> {
    setBusy('save');
    try {
      const saved = await saveDraft();
      if (saved) setToast('Bozza salvata — puoi emetterla quando vuoi');
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * Emissione: salva la bozza, forza il push al server, chiama la RPC
   * issue_shipment (numerazione TRA n/anno per tenant/anno) e registra
   * l'evento append-only shipment_dispatched. Online-only by design.
   */
  async function handleIssue(): Promise<void> {
    if (lines.length === 0) {
      setToast('Aggiungi almeno una riga di merce da trasferire');
      return;
    }
    setBusy('issue');
    try {
      // Offline: salva comunque la bozza e spiega chiaramente il vincolo.
      if (!navigator.onLine) {
        const saved = await saveDraft();
        if (saved) {
          setToast('Serve connessione per emettere il documento; la bozza resta salvata.');
        }
        return;
      }
      const saved = await saveDraft();
      if (!saved) return;
      const { id, expectedPatch, persistedLines } = saved;
      // Payload dell'evento costruito e validato PRIMA della RPC: l'emissione
      // è irreversibile (assegna il numero progressivo), quindi se i dati non
      // passano lo schema abortiamo qui e la bozza resta intatta.
      const buildPayload = (docNumber: number, docYear: number) => ({
        shipment_id: id,
        kind: 'trasferimento' as const,
        doc_number: docNumber,
        doc_year: docYear,
        to_business_id: toBusinessId,
        line_count: persistedLines.length,
        lines_summary: persistedLines.map((l) => ({
          description: l.description,
          batch_lot: l.batch_lot,
          qty: l.qty,
          uom: l.uom,
        })),
        vehicle_plate: vehiclePlate.trim() || null,
      });
      const precheck = ShipmentDispatchedPayloadSchema.safeParse(
        // doc_number/doc_year arrivano solo dopo la RPC: placeholder validi
        // per controllare tutto il resto (righe, lotti, targa, ...).
        buildPayload(1, new Date().getFullYear()),
      );
      if (!precheck.success) {
        const issue = precheck.error.issues[0];
        const where = issue && issue.path.length > 0 ? issue.path.join('.') : 'dati documento';
        setToast(`Impossibile emettere — ${where}: ${issue?.message ?? 'dati non validi'}. La bozza resta salvata.`);
        return;
      }
      if (!profile?.active_tenant_id || !profile.active_business_id) {
        setToast('Nessuna sede attiva');
        return;
      }
      const originBusinessId = profile.active_business_id;
      let stockCommand: StockCommandV1 | null = null;
      if (collectiveFlow || warehouseEnabled) {
        stockCommand = StockCommandV1Schema.parse({
          version: 1,
          // Il documento è l'identità idempotente del suo movimento stock.
          command_id: id,
          tenant_id: profile.active_tenant_id,
          movement_class: 'transfer',
          economic_effect: 'internal',
          reason: 'shipment_transfer',
          from_business_id: profile.active_business_id,
          to_business_id: toBusinessId,
          shipment_id: id,
          lines: persistedLines
            .filter((line): line is ShipmentLine & { food_id: string } => !!line.food_id)
            .map((line, index) => {
              const food = foods.find((candidate) => candidate.id === line.food_id);
              if (!food?.default_uom
                || food.default_uom.trim().toLowerCase() !== line.uom.trim().toLowerCase()) {
                throw new Error(
                  `UdM non coerente per "${line.description}": il documento usa ${line.uom}, `
                  + `il prodotto usa ${food?.default_uom ?? 'UdM assente'}. Correggi prima di emettere.`,
                );
              }
              const origin = findExactThenLegacyStockEvent(
                stockIn as StockEventRow[],
                originBusinessId,
                allowLegacyUnscopedBusiness,
                (event) => (
                  event.payload.food_id === line.food_id
                  && (event.payload.batch_lot ?? '').trim() === line.batch_lot.trim()
                  && event.payload.void_compensation !== true
                ),
              );
              const balance = stockProjection.balances.find((candidate) => (
                candidate.foodId === line.food_id
                && (candidate.batchLot ?? '') === line.batch_lot.trim()
              ));
              const carryingCost = balance
                ? projectedCarryingCost(balance)
                : { unitCost: null, currency: 'EUR' };
              return {
                food_id: line.food_id,
                batch_lot: line.batch_lot,
                expiration_date: origin?.payload.expiration_date ?? null,
                supplier_id: origin?.payload.supplier_id ?? null,
                document_id: origin?.payload.document_id ?? null,
                storage_unit_id: null,
                line_ref: `shipment:${id}:${index + 1}`,
                lot_action: 'recorded_only',
                source_qty: line.qty,
                source_uom: line.uom,
                source_unit_price: carryingCost.unitCost,
                source_line_total: carryingCost.unitCost == null
                  ? null
                  : carryingCost.unitCost * line.qty,
                currency: carryingCost.currency,
                stock_uom: line.uom,
                conversion: { factor: 1, source: 'identity' },
              };
            }),
        }) as StockCommandV1;
      }
      // Conferma esattamente QUESTA bozza. Il vecchio controllo su
      // pendingCount poteva osservare 0 prima che il tick in background avesse
      // registrato l'operazione, emettendo contro una riga server assente o
      // stantia. Un conflitto OCC non è una conferma: la bozza resta salvata e
      // va ricaricata prima di assegnare un numero ufficiale.
      const confirmation = await getSyncEngine().confirmMasterPushed(
        'shipments',
        id,
        { timeoutMs: 12_000, acceptConflict: false, expectedPatch },
      );
      if (!confirmation.confirmed) {
        const conflictHint = confirmation.reason === 'master_version_conflict'
          ? ' La bozza è stata modificata altrove: ricarica la pagina.'
          : '';
        setToast(`Sincronizzazione non riuscita: il documento non è stato emesso.${conflictHint} La bozza resta salvata.`);
        return;
      }
      if (!collectiveFlow) {
        const finalCheck = restaurantLinesForPersistence();
        if (finalCheck.error
          || JSON.stringify(finalCheck.lines) !== JSON.stringify(persistedLines)) {
          setToast(
            finalCheck.error
              ? `${finalCheck.error}. Riapri e risalva la bozza prima di emettere.`
              : 'Produzione, lotto o configurazione magazzino sono cambiati. Riapri e risalva la bozza prima di emettere.',
          );
          return;
        }
      }
      // Numerazione e out/in entrano nella stessa transazione PostgreSQL: se
      // anche una sola gamba stock fallisce, il documento resta bozza e il
      // contatore non avanza.
      const res = stockCommand
        ? await getSyncEngine().issueShipmentWithStock(id, stockCommand)
        : await getSyncEngine().issueShipmentWithoutStock(id);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['events'] }),
        queryClient.invalidateQueries({ queryKey: ['masters'] }),
      ]);
      // Master issued, movimenti e audit shipment_dispatched sono già nel
      // writeback atomico applicato dal SyncEngine.
      setIssuedLocal((prev) => ({ ...prev, [id]: { doc_number: res.doc_number!, doc_year: res.doc_year! } }));
      setJustIssued({ id, doc_number: res.doc_number, doc_year: res.doc_year });
      setToast(`Emesso ${formatShipmentNumber(res.doc_number, res.doc_year, 'trasferimento')}`);
      resetForm();
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(null);
    }
  }

  // "Apri PDF" via edge function export-ddt (solo documenti emessi). Stesso
  // pattern dei registri su TodayPage: invoke → pdf_url firmato → window.open.
  async function openPdf(shipmentId: string): Promise<void> {
    const shipment = shipments.find((candidate) => candidate.id === shipmentId);
    if (!shipment || !isActiveBusinessTransfer(shipment)) {
      setToast('Il documento appartiene a un’altra sede');
      return;
    }
    if (!navigator.onLine) {
      setToast('Serve connessione per generare il PDF');
      return;
    }
    setBusy('pdf');
    try {
      const { data, error } = await getSupabase().functions.invoke<{ pdf_url?: string }>(
        'export-ddt',
        { body: { shipment_id: shipmentId } },
      );
      if (error) throw error;
      if (!data?.pdf_url) throw new Error('Nessun URL restituito');
      window.open(data.pdf_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setBusy(null);
    }
  }

  /**
   * T-295 — Annulla un trasferimento EMESSO. T-228 aveva aggiunto annullo +
   * compensazione magazzino ai soli DDT in uscita (ShipmentsPage.voidDdt),
   * lasciando i trasferimenti interni senza modo di essere corretti/annullati.
   *
   * Append-only (mai UPDATE/DELETE sugli eventi): la giacenza si RIPRISTINA con
   * eventi COMPENSATIVI, l'inverso esatto di ciò che ha scritto l'emissione
   * (handleIssue): stock_in di ritorno al magazzino della sede MITTENTE +
   * stock_out dal magazzino della sede di DESTINAZIONE. Il documento resta in
   * archivio come ANNULLATO (status='void'), col motivo annotato.
   */
  async function voidTransfer(s: ShipmentRow, reason: string): Promise<void> {
    if (voidBusy.current) return; // fix #5: già in corso → no doppia compensazione
    if (!isActiveBusinessTransfer(s) || s.status !== 'issued') {
      setToast('Il trasferimento appartiene a un’altra sede o non è annullabile');
      return;
    }
    voidBusy.current = true;
    try {
      const motive = reason.trim() || 'non specificato';
      if (!navigator.onLine) {
        throw new Error('Serve connessione per annullare un trasferimento emesso');
      }
      const engine = getSyncEngine();
      let partial = false;
      try {
        const preparation = await engine.prepareShipmentStockVoid(s.id);
        partial = preparation.movements.some(
          (movement) => movement.max_reversible_qty < movement.stock_qty,
        );
        const reverseCommand = buildShipmentStockVoidCommand(
          preparation,
          await shipmentStockVoidCommandIdFor(s.tenant_id, s.id),
          motive,
        );
        await engine.voidShipmentWithStock(s.id, motive, reverseCommand);
      } catch (prepareError) {
        const message = prepareError instanceof Error
          ? prepareError.message
          : String(prepareError);
        if (collectiveFlow
          || !message.includes('shipment_stock_void_prepare_v1_receipt_missing')) {
          throw prepareError;
        }
        // Documento ristorante emesso con magazzino OFF: il wrapper server
        // verifica che non esistano receipt/eventi stock prima dell'annullo.
        await engine.voidShipmentWithoutStock(s.id, motive);
      }
      setVoidedLocal((current) => new Set(current).add(s.id));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['events'] }),
        queryClient.invalidateQueries({ queryKey: ['masters'] }),
      ]);
      void engine.pull();
      setToast(
        `${formatShipmentNumber(s.doc_number, s.doc_year, 'trasferimento')} annullato`
        + (partial ? ' — ripristinata solo la merce ancora presente a destinazione.' : ''),
      );
      setVoidTarget(null);
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      voidBusy.current = false;
    }
  }

  /**
   * T-295 — Elimina una BOZZA di trasferimento (archiviazione, mai delete
   * fisico). Le bozze non hanno generato eventi (riserva soltanto) → nessuna
   * compensazione sulla hash chain. Mirror di ShipmentsPage.deleteDraft.
   */
  async function deleteDraft(s: ShipmentRow): Promise<void> {
    try {
      const current = shipments.find((x) => x.id === s.id) ?? s;
      if (!isActiveBusinessTransfer(current) || current.status !== 'draft') {
        setToast('La bozza appartiene a un’altra sede o non è più eliminabile');
        setDeleteTarget(null);
        return;
      }
      await enqueueMaster.mutateAsync({
        table: 'shipments',
        id: s.id,
        op: 'archive',
        base_version: current.version ?? 0,
        patch: { id: s.id },
      });
      setToast('Bozza eliminata');
      setDeleteTarget(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  if (!collectiveFlow && transportAccess.checking) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const moduleEnabled = collectiveFlow ? collettivaEnabled : transportAccess.enabled;
  if (!moduleEnabled) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref={collectiveFlow ? '/today' : '/documents/transport'} />
            </IonButtons>
            <IonTitle>{collectiveFlow ? 'Trasferimento merce' : 'Trasferimento interno'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontSize: 14 }}>
            {collectiveFlow
              ? 'Modulo collettiva non attivo. Chiedi al titolare di abilitarlo dalle impostazioni.'
              : transportAccess.error
                ? 'Impossibile verificare in modo sicuro l’accesso ai Documenti di trasporto. Riprova con connessione disponibile.'
                : 'Modulo Documenti di trasporto non attivo per questo locale.'}
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={collectiveFlow ? '/today' : '/documents/transport'} />
          </IonButtons>
          <IonTitle>{collectiveFlow ? 'Trasferimento merce' : 'Trasferimento interno'}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        <div style={{ padding: '12px 12px 32px' }}>
          {/* T-221: banner — ogni sede ha il suo magazzino, l'emissione muove la merce */}
          <div
            style={{
              background: !collectiveFlow && warehouseEnabled ? '#ecfdf5' : '#eff6ff',
              border: `1px solid ${!collectiveFlow && warehouseEnabled ? '#a7f3d0' : '#bfdbfe'}`,
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 12.5,
              color: !collectiveFlow && warehouseEnabled ? '#065f46' : '#1e40af',
              marginBottom: 12,
            }}
          >
            <IonIcon
              icon={informationCircleOutline}
              aria-hidden="true"
              style={{ marginRight: 6, verticalAlign: '-2px' }}
            />
            {collectiveFlow
              ? <>L'emissione del trasferimento scarica il magazzino di questa sede e
                  carica quello della sede di destinazione. Le bozze riservano la merce
                  (sparisce dal disponibile) senza muoverla finché non emetti.</>
              : warehouseEnabled
                ? <>Magazzino attivo: puoi trasferire solo lotti ottenuti dalle lavorazioni
                    della sede e collegati al relativo prodotto finito. La bozza riserva la quantità;
                    l'emissione scarica questa sede e carica quella di destinazione.</>
                : <>Magazzino non attivo: il documento conserva lavorazione, lotto e quantità
                    dichiarata dalla produzione, senza simulare disponibilità o movimenti. Le bozze
                    non riservano quantità.</>}
          </div>

          {/* Card emissione appena conclusa */}
          {justIssued && (
            <div
              style={{
                background: '#ecfdf5',
                border: '1px solid #a7f3d0',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ flex: 1, fontSize: 13, color: '#065f46', fontWeight: 600 }}>
                ✓ {formatShipmentNumber(justIssued.doc_number, justIssued.doc_year, 'trasferimento')} emesso
              </span>
              <IonButton
                size="small"
                fill="outline"
                color="success"
                disabled={busy === 'pdf'}
                onClick={() => { void openPdf(justIssued.id); }}
                style={{ textTransform: 'none' } as React.CSSProperties}
              >
                {busy === 'pdf' ? <IonSpinner name="dots" /> : 'Apri PDF'}
              </IonButton>
            </div>
          )}

          {otherBusinesses.length === 0 ? (
            <div style={{ background: '#fff', padding: 16, borderRadius: 14, color: '#64748b', fontSize: 14 }}>
              Configura una seconda sede (impianto) nelle impostazioni per registrare i
              trasferimenti interni.
            </div>
          ) : (
            <>
              {/* ── 1 · Sede di destinazione ── */}
              <h3 style={{ ...sectionHeaderStyle, marginTop: 4 }}>1 · Sede di destinazione</h3>
              <div style={stripedCardStyle}>
                <div style={stripeStyle(!!toBusinessId)} />
                <div style={{ flex: 1, minWidth: 0, padding: '2px 4px' }}>
                  <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                    <IonSelect
                      label="Sede di destinazione"
                      labelPlacement="stacked"
                      interface="popover"
                      placeholder="Scegli la sede"
                      value={toBusinessId}
                      onIonChange={(ev) => setToBusinessId(String(ev.detail.value ?? ''))}
                    >
                      {otherBusinesses.map((b) => (
                        <IonSelectOption key={b.id} value={b.id}>{b.name}</IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                </div>
              </div>

              {/* ── 2 · Merce da trasferire ── */}
              <h3 style={sectionHeaderStyle}>
                2 · {collectiveFlow ? 'Merce da trasferire' : 'Prodotti finiti da trasferire'} ({lines.length})
              </h3>
              {lines.length === 0 && (
                <div style={stripedCardStyle}>
                  <div style={stripeStyle(false)} />
                  <div style={{ flex: 1, padding: '14px', color: '#64748b', fontSize: 13, fontStyle: 'italic' }}>
                    {collectiveFlow
                      ? 'Nessuna merce aggiunta: scegli i lotti dal magazzino qui sotto.'
                      : 'Nessun prodotto aggiunto: richiama una lavorazione di oggi o dallo storico.'}
                  </div>
                </div>
              )}
              {lines.length > 0 && (
                <div style={stripedCardStyle}>
                  <div style={stripeStyle(true)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {lines.map((l) => (
                      <div
                        key={l.production_id ?? `${l.food_id ?? ''}|${l.batch_lot}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 14px',
                          borderBottom: '1px solid #f1f5f9',
                        }}
                      >
                        <span
                          role="button"
                          onClick={() => editLine(l)}
                          style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                        >
                          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {l.description}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 1 }}>
                            Lotto {l.batch_lot} · {fmtQty(l.qty)} {l.uom}
                          </span>
                        </span>
                        <IonButton
                          size="small"
                          fill="clear"
                          color="danger"
                          aria-label={`Rimuovi ${l.description}`}
                          onClick={() => removeLine(l)}
                        >
                          <IonIcon icon={trashOutline} slot="icon-only" />
                        </IonButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Picker merce: lotti disponibili / produzioni ristorante ── */}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', margin: '12px 4px 2px' }}>
                {collectiveFlow ? 'Aggiungi merce dal magazzino' : 'Richiama lavorazioni e lotti'}
              </div>
              {!collectiveFlow && (
                <IonSegment
                  value={productionScope}
                  onIonChange={(ev) => setProductionScope(
                    (ev.detail.value as 'today' | 'history') ?? 'today',
                  )}
                  style={{ marginTop: 8 }}
                >
                  <IonSegmentButton value="today">
                    <IonLabel>Oggi ({restaurantCandidateBuckets.today.length})</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="history">
                    <IonLabel>Storico ({restaurantCandidateBuckets.history.length})</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
              )}
              <IonSearchbar
                placeholder={collectiveFlow ? 'Cerca alimento' : 'Cerca prodotto o lotto'}
                value={lotSearch}
                onIonInput={(ev) => setLotSearch(String(ev.detail.value ?? ''))}
                style={{ padding: 0 }}
              />
              {collectiveFlow ? (
                filteredLots.length === 0 ? (
                  <div style={{ background: '#fff', padding: 14, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
                    {lotSearch
                      ? 'Nessun lotto disponibile per questa ricerca'
                      : 'Nessun lotto con giacenza disponibile in magazzino'}
                  </div>
                ) : (
                  <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
                    {filteredLots.slice(0, 60).map((l) => {
                      const inLines = lines.some(
                        (x) => x.food_id === l.food_id && x.batch_lot === l.lot,
                      );
                      return (
                        <div
                          key={`${l.food_id}|${l.lot}`}
                          role="button"
                          onClick={() => openQtyModal(l)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            borderBottom: '1px solid #f1f5f9',
                            cursor: 'pointer',
                            background: inLines ? '#f0fdf4' : '#fff',
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {l.name}
                            </span>
                            <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 1 }}>
                              Lotto {l.lot} · disponibili {fmtQty(l.qty)} {l.uom || foodUom(l.food_id)}
                            </span>
                          </span>
                          {inLines && (
                            <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a' }}>✓ aggiunto</span>
                          )}
                        </div>
                      );
                    })}
                    {filteredLots.length > 60 && (
                      <div style={{ padding: '8px 14px', fontSize: 12, color: '#94a3b8' }}>
                        +{filteredLots.length - 60} altri lotti — affina la ricerca
                      </div>
                    )}
                  </div>
                )
              ) : restaurantLots.length === 0 ? (
                <div style={{ background: '#fff', padding: 14, borderRadius: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
                  {lotSearch
                    ? 'Nessuna lavorazione corrisponde alla ricerca.'
                    : productionScope === 'history'
                      ? 'Nessuna lavorazione storica disponibile.'
                      : 'Nessuna lavorazione registrata oggi (o sono già tutte nel documento).'}
                </div>
              ) : (
                <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', maxHeight: 360, overflowY: 'auto' }}>
                  {restaurantLots.slice(0, 60).map((candidate) => {
                    const disabled = !!candidate.disabledReason;
                    return (
                      <div
                        key={candidate.production.id}
                        role="button"
                        aria-disabled={disabled}
                        onClick={() => openRestaurantQtyModal(candidate)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '10px 14px',
                          borderBottom: '1px solid #f1f5f9',
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          opacity: disabled ? 0.62 : 1,
                          background: '#fff',
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {candidate.production.output_name}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 1 }}>
                            Lotto {candidate.lot} · {new Date(candidate.production.produced_at).toLocaleDateString('it-IT')}
                          </span>
                          <span style={{ display: 'block', fontSize: 12, color: disabled ? '#b45309' : '#64748b', marginTop: 1 }}>
                            {candidate.disabledReason
                              ?? (warehouseEnabled
                                ? `disponibili ${fmtQty(candidate.availableQty ?? 0)} ${candidate.uom}`
                                : `quantità dichiarata ${fmtQty(candidate.declaredQty ?? 0)} ${candidate.uom}`)}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── 3 · Trasporto e note ── */}
              <h3 style={sectionHeaderStyle}>3 · Trasporto e note</h3>
              <div style={{ ...plainCardStyle, padding: '2px 4px' }}>
                <IonItem style={{ '--background': 'transparent' } as React.CSSProperties}>
                  <IonInput
                    label="Targa automezzo (opzionale)"
                    labelPlacement="stacked"
                    placeholder="es. AB123CD"
                    maxlength={20}
                    value={vehiclePlate}
                    onIonInput={(ev) => setVehiclePlate(String(ev.detail.value ?? ''))}
                  />
                </IonItem>
                <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                  <IonLabel position="stacked">Note (opzionale)</IonLabel>
                  <IonTextarea
                    autoGrow
                    rows={2}
                    placeholder="Annotazioni sul trasferimento"
                    maxlength={500}
                    value={notes}
                    onIonInput={(ev) => setNotes(String(ev.detail.value ?? ''))}
                  />
                </IonItem>
              </div>

              {/* ── Azioni: secondaria (bozza) sopra, CTA primaria unica sotto ── */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 18 }}>
                <IonButton
                  expand="block"
                  fill="outline"
                  size="small"
                  disabled={busy !== null}
                  onClick={() => { void handleSaveDraft(); }}
                  style={{ textTransform: 'none' } as React.CSSProperties}
                >
                  <IonIcon icon={saveOutline} slot="start" />
                  {busy === 'save' ? <IonSpinner name="dots" /> : editing ? 'Aggiorna bozza' : 'Salva bozza'}
                </IonButton>
                <IonButton
                  expand="block"
                  disabled={busy !== null}
                  onClick={() => { void handleIssue(); }}
                  style={{ height: 52, '--border-radius': '14px', fontWeight: 700, textTransform: 'none' } as React.CSSProperties}
                >
                  <IonIcon icon={paperPlaneOutline} slot="start" />
                  {busy === 'issue' ? <IonSpinner name="dots" /> : 'EMETTI TRASFERIMENTO'}
                </IonButton>
                <p style={{ fontSize: 11.5, color: '#94a3b8', margin: '0 4px' }}>
                  L'emissione assegna il numero progressivo (TRA n/anno) e richiede la
                  connessione. Offline la bozza resta salvata e puoi emetterla dopo.
                </p>
              </div>
            </>
          )}

          {/* ── Trasferimenti recenti ── */}
          <h3 style={{ ...sectionHeaderStyle, margin: '22px 4px 8px' }}>
            Trasferimenti recenti
          </h3>
          {recentTransfers.length === 0 ? (
            <div style={{ ...plainCardStyle, padding: 14, color: '#64748b', fontStyle: 'italic', fontSize: 13 }}>
              Nessun trasferimento registrato
            </div>
          ) : (
            <div style={plainCardStyle}>
              {recentTransfers.map((s) => {
                const local = issuedLocal[s.id];
                const isIssued = s.status === 'issued' || !!local;
                const docNumber = s.doc_number ?? local?.doc_number ?? null;
                const docYear = s.doc_year ?? local?.doc_year ?? null;
                // Solo presentazione: stato effettivo mostrato dal badge.
                const shownStatus: TransferStatus = s.status === 'void' || voidedLocal.has(s.id)
                  ? 'void'
                  : isIssued ? 'issued' : 'draft';
                const title = shownStatus === 'draft'
                  ? 'Trasferimento'
                  : formatShipmentNumber(docNumber, docYear, 'trasferimento');
                const when = (s.issued_at ?? s.created_at ?? '').slice(0, 10);
                const lineCount = Array.isArray(s.lines) ? s.lines.length : 0;
                return (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 14px',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                          {title}
                        </span>
                        <span style={statusBadgeStyle(shownStatus)}>{STATUS_BADGE[shownStatus].label}</span>
                      </span>
                      <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        → {businessName(s.to_business_id)}
                        {when ? ` · ${when}` : ''}
                        {` · ${lineCount} rig${lineCount === 1 ? 'a' : 'he'}`}
                      </span>
                    </span>
                    {s.status === 'draft' && !local && (
                      <>
                        <IonButton
                          size="small"
                          fill="outline"
                          onClick={() => resumeDraft(s)}
                          style={{ textTransform: 'none' } as React.CSSProperties}
                        >
                          Riprendi
                        </IonButton>
                        <IonButton
                          size="small"
                          fill="outline"
                          color="danger"
                          aria-label="Elimina bozza"
                          onClick={() => setDeleteTarget(s)}
                          style={{ textTransform: 'none' } as React.CSSProperties}
                        >
                          <IonIcon icon={trashOutline} slot="start" />
                          Elimina
                        </IonButton>
                      </>
                    )}
                    {isIssued && shownStatus !== 'void' && (
                      <IonButton
                        size="small"
                        fill="outline"
                        disabled={busy === 'pdf'}
                        onClick={() => { void openPdf(s.id); }}
                        style={{ textTransform: 'none' } as React.CSSProperties}
                      >
                        <IonIcon icon={documentTextOutline} slot="start" />
                        PDF
                      </IonButton>
                    )}
                    {/* T-295: annullo del trasferimento emesso (server-confirmed) */}
                    {shownStatus === 'issued' && s.status === 'issued' && (
                      <IonButton
                        size="small"
                        fill="outline"
                        color="danger"
                        aria-label="Annulla trasferimento"
                        onClick={() => setVoidTarget(s)}
                        style={{ textTransform: 'none' } as React.CSSProperties}
                      >
                        <IonIcon icon={closeOutline} slot="start" />
                        Annulla
                      </IonButton>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Modale quantità ── */}
        <IonModal
          isOpen={!!qtyModal}
          onDidDismiss={() => setQtyModal(null)}
          initialBreakpoint={0.45}
          breakpoints={[0, 0.45, 0.75]}
        >
          <div style={{ padding: 20 }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>{qtyModal?.foodName}</h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 12px' }}>
              Lotto {qtyModal?.lot} · {qtyModal?.limitKind === 'production'
                ? 'quantità dichiarata dalla lavorazione'
                : 'disponibili'} {qtyModal ? fmtQty(qtyModal.maxQty) : ''} {qtyModal?.uom}
            </p>
            <IonItem>
              <IonInput
                label={`Quantità da trasferire (${qtyModal?.uom ?? ''})`}
                labelPlacement="stacked"
                type="text"
                inputmode="decimal"
                value={qtyInput}
                onIonInput={(ev) => setQtyInput(String(ev.detail.value ?? ''))}
              />
            </IonItem>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <IonButton
                expand="block"
                fill="outline"
                style={{ flex: 1, textTransform: 'none' } as React.CSSProperties}
                onClick={() => setQtyModal(null)}
              >
                Annulla
              </IonButton>
              <IonButton
                expand="block"
                style={{ flex: 1, textTransform: 'none' } as React.CSSProperties}
                onClick={confirmQty}
              >
                Aggiungi riga
              </IonButton>
            </div>
          </div>
        </IonModal>

        {/* T-295: annulla trasferimento emesso (resta in archivio come ANNULLATO) */}
        <IonAlert
          isOpen={!!voidTarget}
          header="Annulla trasferimento"
          message={
            voidTarget
              ? !collectiveFlow && !transferHasStockLines(voidTarget)
                ? `Annullare ${formatShipmentNumber(voidTarget.doc_number, voidTarget.doc_year, 'trasferimento')} verso ${businessName(voidTarget.to_business_id)}? Il documento non ha movimenti di magazzino e resta in archivio come ANNULLATO.`
                : `Annullare ${formatShipmentNumber(voidTarget.doc_number, voidTarget.doc_year, 'trasferimento')} verso ${businessName(voidTarget.to_business_id)}? La quantità ancora presente a destinazione rientra nel magazzino di partenza e il documento resta in archivio come ANNULLATO.`
              : ''
          }
          inputs={[{ name: 'reason', type: 'textarea', placeholder: "Motivo dell'annullamento *" }]}
          buttons={[
            { text: 'Indietro', role: 'cancel' },
            {
              text: 'Annulla trasferimento',
              role: 'destructive',
              handler: (vals: { reason?: string }) => {
                if (voidTarget) void voidTransfer(voidTarget, String(vals?.reason ?? ''));
              },
            },
          ]}
          onDidDismiss={() => setVoidTarget(null)}
        />

        {/* T-295: elimina bozza (archiviazione, mai delete fisico) */}
        <IonAlert
          isOpen={!!deleteTarget}
          header="Elimina bozza"
          message={deleteTarget ? `Eliminare la bozza di trasferimento verso ${businessName(deleteTarget.to_business_id)}?` : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Elimina', role: 'destructive', handler: () => { if (deleteTarget) void deleteDraft(deleteTarget); } },
          ]}
          onDidDismiss={() => setDeleteTarget(null)}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={2800}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}

export function TransferPage(): JSX.Element {
  return <TransferPageContent flow="collective" />;
}

export function RestaurantTransferPage(): JSX.Element {
  return <TransferPageContent flow="restaurant" />;
}
